const customerLearningPathPattern =
  /^\/(en|ru)\/crm\/(?:user-profiles|customers)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/learning$/iu;
const legacyCustomerCoursePathPattern =
  /^\/(en|ru)\/crm\/(?:user-profiles|customers)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/learning\/courses\/[0-9a-f-]+$/iu;
const courseProfilePathPattern =
  /^\/(en|ru)\/crm\/courses\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
const courseProfileSearchKeys = [
  'courseProfileCertificationState',
  'courseProfileCursor',
  'courseProfileCursorHistory',
  'courseProfilePage',
  'courseProfilePeopleView',
  'courseProfileQ',
  'courseProfileSort',
  'courseProfileTab',
] as const;

function splitApplicationPath(
  value: string
): { pathname: string; search: string; hash: string } | undefined {
  if (!value.startsWith('/') || value.startsWith('//')) return undefined;

  const hashIndex = value.indexOf('#');
  const withoutHash = hashIndex === -1 ? value : value.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : value.slice(hashIndex);
  const queryIndex = withoutHash.indexOf('?');
  return queryIndex === -1
    ? { pathname: withoutHash, search: '', hash }
    : { pathname: withoutHash.slice(0, queryIndex), search: withoutHash.slice(queryIndex), hash };
}

function withoutCourseProfileSearch(search: string): string {
  if (search.length === 0) return '';
  const retained = search
    .slice(1)
    .split('&')
    .filter((entry) => {
      const [rawKey = ''] = entry.split('=', 1);
      try {
        return !(courseProfileSearchKeys as readonly string[]).includes(
          decodeURIComponent(rawKey.replaceAll('+', ' '))
        );
      } catch {
        return true;
      }
    });
  return retained.length === 0 ? '' : `?${retained.join('&')}`;
}

export function normalizeCrmCourseProfileReturnTo(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  const parsed = splitApplicationPath(value);
  if (parsed === undefined || !customerLearningPathPattern.test(parsed.pathname)) return undefined;
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

/**
 * Allows child profiles to return only to a Course Profile URL, including its
 * URL-backed tab, search, filters, and pagination state.
 */
export function normalizeCrmCourseProfileNavigationReturnTo(
  value: string | undefined
): string | undefined {
  if (value === undefined) return undefined;
  const parsed = splitApplicationPath(value);
  if (parsed === undefined || !courseProfilePathPattern.test(parsed.pathname)) return undefined;
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export function customerLearningReturnToFromLegacyCourseLocation(
  value: string | undefined
): string | undefined {
  if (value === undefined) return undefined;
  const parsed = splitApplicationPath(value);
  if (parsed === undefined) return undefined;
  const match = legacyCustomerCoursePathPattern.exec(parsed.pathname);
  if (match === null) return undefined;
  const locale = match.at(1);
  const customerId = match.at(2);
  if (locale === undefined || customerId === undefined) return undefined;
  const pathname = `/${locale}/crm/user-profiles/${customerId}/learning`;
  return normalizeCrmCourseProfileReturnTo(
    `${pathname}${withoutCourseProfileSearch(parsed.search)}${parsed.hash}`
  );
}
